import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useToastController } from "@tamagui/toast";
import { AlertDialogNotification } from "@medbillai/ui";
export type AlertType = "success" | "error" | "warning";
export type Alert = {
  type: AlertType;
  message: string;
  duration?: number;
};
export type AlertDialog = {
  title: string;
  message: string;
  acceptText?: string;
  onAccept?: () => void;
  // defaults to false
  cancellable?: boolean;
  cancelThin?: boolean;
};
export interface AlertContextInterface {
  showAlert: (alert: Alert) => void;
  showAlertDialog: (alert: AlertDialog) => void;
  isAlertDialogOpen: boolean;
}
export const initialContext: AlertContextInterface = {
  showAlert: () => {
    console.error("Context not initialized");
  },
  showAlertDialog: () => {
    console.error("Context not initialized");
  },
  isAlertDialogOpen: false
};
export const AlertContext = createContext<AlertContextInterface>(initialContext);
export interface AlertProviderProps {
  children?: React.ReactNode;
}
const alertTitles: Record<AlertType, string> = {
  success: "Success",
  error: "Error",
  warning: "Warning"
};
export const AlertProvider = ({
  children
}: AlertProviderProps) => {
  // Initializing with an empty alert to mount the component
  const [alertDialog, setAlertDialog] = useState<AlertDialog>({
    title: "",
    message: ""
  });
  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const [mounted, setMounted] = useState(false);
  const toastController = useToastController();

  // Only render AlertDialog on client to avoid SSR hydration issues
  useEffect(() => {
    setMounted(true);
  }, []);
  const showAlert = useCallback(({
    type,
    message,
    duration
  }: Alert) => {
    const title = alertTitles[type];
    toastController.show(title, {
      message,
      duration: duration || 5000,
      burntOptions: {
        haptic: type
      }
    });
  }, [toastController]);
  const showAlertDialog = useCallback(({
    title,
    message,
    onAccept,
    acceptText = "Ok",
    cancellable = false,
    cancelThin = false
  }: AlertDialog) => {
    setAlertDialog({
      title,
      message,
      onAccept,
      acceptText,
      cancellable,
      cancelThin
    });
    setOpenAlertDialog(true);
  }, []);
  const contextValue = useMemo<AlertContextInterface>(() => ({
    showAlert,
    showAlertDialog,
    isAlertDialogOpen: openAlertDialog
  }), [showAlert, showAlertDialog, openAlertDialog]);
  return <AlertContext.Provider value={contextValue} data-sentry-element="AlertContext.Provider" data-sentry-component="AlertProvider" data-sentry-source-file="AlertProvider.tsx">
      {mounted ? <AlertDialogNotification open={openAlertDialog} onOpenChange={() => {
      setOpenAlertDialog(false);
    }} {...alertDialog} /> : null}
      {children}
    </AlertContext.Provider>;
};
export const useAlerts = (context = AlertContext): AlertContextInterface => useContext(context);